import * as i1 from '@angular/animations';
import { FlexibleConnectedPositionStrategy } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import * as i0 from '@angular/core';
import { ElementRef, Component, ChangeDetectionStrategy, Inject, Input, ViewChild, HostBinding } from '@angular/core';
import { NgDocFocusControlComponent } from '@ng-doc/ui-kit/components/focus-control';
import { NgDocOverlayPointerComponent } from '@ng-doc/ui-kit/components/overlay-pointer';
import { NgDocEventSwitcherDirective } from '@ng-doc/ui-kit/directives/event-switcher';
import { NgDocFocusCatcherDirective } from '@ng-doc/ui-kit/directives/focus-catcher';
import { toElement } from '@ng-doc/ui-kit/helpers';
import { ngDocZoneOptimize } from '@ng-doc/ui-kit/observables';
import { NgDocOverlayUtils, NgDocFocusUtils } from '@ng-doc/ui-kit/utils';
import * as i2 from '@tinkoff/ng-polymorpheus';
import { PolymorpheusOutletDirective, PolymorpheusModule } from '@tinkoff/ng-polymorpheus';
import { Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
const _c0 = ["contentContainer"];
const _c1 = () => ["focusin", "focusout", "keydown", "scroll"];
function NgDocOverlayContainerComponent_ng_container_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const text_r1 = ctx.polymorpheusOutlet;
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(text_r1);
  }
}
let NgDocOverlayContainerComponent = /*#__PURE__*/(() => {
  class NgDocOverlayContainerComponent {
    constructor(elementRef, documentRef, changeDetectorRef, ngZone, animationBuilder) {
      this.elementRef = elementRef;
      this.documentRef = documentRef;
      this.changeDetectorRef = changeDetectorRef;
      this.ngZone = ngZone;
      this.animationBuilder = animationBuilder;
      this.content = '';
      this.relativePosition = null;
      this.animationEvent$ = new Subject();
      this.isOpened = true;
    }
    ngOnInit() {
      if (this.config?.positionStrategy instanceof FlexibleConnectedPositionStrategy) {
        this.config.positionStrategy.positionChanges.pipe(distinctUntilChanged((a, b) => a.connectionPair === b.connectionPair), ngDocZoneOptimize(this.ngZone)).subscribe(change => {
          this.currentPosition = NgDocOverlayUtils.getOverlayPosition(change.connectionPair);
          this.relativePosition = NgDocOverlayUtils.getRelativePosition(this.currentPosition);
          this.changeDetectorRef.markForCheck();
        });
      }
    }
    ngAfterViewInit() {
      this.runAnimation(this.config?.openAnimation || []);
    }
    get contactBorder() {
      return !!this.config?.contactBorder;
    }
    get isFocused() {
      return !!this.focusCatcher?.focused;
    }
    get animationEvent() {
      return this.animationEvent$.asObservable();
    }
    get overlayAlign() {
      return this.currentPosition ? NgDocOverlayUtils.getPositionAlign(NgDocOverlayUtils.toConnectedPosition(this.currentPosition)) : null;
    }
    close() {
      if (this.isOpened) {
        this.runAnimation(this.config?.closeAnimation || [], true);
        this.isOpened = false;
        this.changeDetectorRef.markForCheck();
      }
    }
    focus() {
      if (this.contentContainer) {
        NgDocFocusUtils.focusClosestElement(toElement(this.contentContainer), toElement(this.contentContainer));
      }
    }
    markForCheck() {
      this.changeDetectorRef.markForCheck();
    }
    runAnimation(animation, close = false) {
      const player = this.animationBuilder.build(animation).create(this.elementRef.nativeElement);
      player.onStart(() => this.animationEvent$.next(close ? 'beforeClose' : 'beforeOpen'));
      player.onDone(() => this.animationEvent$.next(close ? 'afterClose' : 'afterOpen'));
      player.play();
    }
    ngOnDestroy() {
      if (this.isFocused && this.config && this.config.viewContainerRef) {
        NgDocFocusUtils.focusClosestElement(this.config.viewContainerRef.element.nativeElement, this.documentRef.body, false);
      }
    }
    static {
      this.ɵfac = function NgDocOverlayContainerComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgDocOverlayContainerComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(DOCUMENT), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i1.AnimationBuilder));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NgDocOverlayContainerComponent,
        selectors: [["ng-doc-overlay-container"]],
        viewQuery: function NgDocOverlayContainerComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(_c0, 7, ElementRef);
            i0.ɵɵviewQuery(NgDocFocusCatcherDirective, 5);
            i0.ɵɵviewQuery(PolymorpheusOutletDirective, 7);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.contentContainer = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.focusCatcher = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.outlet = _t.first);
          }
        },
        hostVars: 2,
        hostBindings: function NgDocOverlayContainerComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵattribute("data-ng-doc-overlay-position", ctx.relativePosition)("data-ng-doc-overlay-with-contact-border", ctx.contactBorder);
          }
        },
        inputs: {
          content: "content",
          config: "config"
        },
        standalone: true,
        features: [i0.ɵɵStandaloneFeature],
        decls: 5,
        vars: 9,
        consts: [["contentContainer", ""], [3, "overlayPosition", "overlayAlign", "withPointer", "ngDocEventSwitcher", "events"], ["ngDocFocusCatcher", "", 3, "focusHost"], [1, "ng-doc-overlay-content", 3, "tabIndex"], [4, "polymorpheusOutlet"]],
        template: function NgDocOverlayContainerComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "ng-doc-overlay-pointer", 1)(1, "ng-doc-focus-control", 2)(2, "div", 3, 0);
            i0.ɵɵtemplate(4, NgDocOverlayContainerComponent_ng_container_4_Template, 2, 1, "ng-container", 4);
            i0.ɵɵelementEnd()()();
          }
          if (rf & 2) {
            let tmp_4_0;
            let tmp_6_0;
            i0.ɵɵproperty("overlayPosition", ctx.relativePosition)("overlayAlign", ctx.overlayAlign)("withPointer", !!(ctx.config == null ? null : ctx.config.withPointer))("ngDocEventSwitcher", (tmp_4_0 = ctx.config == null ? null : ctx.config.viewContainerRef == null ? null : ctx.config.viewContainerRef.element == null ? null : ctx.config.viewContainerRef.element.nativeElement) !== null && tmp_4_0 !== undefined ? tmp_4_0 : null)("events", i0.ɵɵpureFunction0(8, _c1));
            i0.ɵɵadvance();
            i0.ɵɵproperty("focusHost", (tmp_6_0 = ctx.config == null ? null : ctx.config.viewContainerRef == null ? null : ctx.config.viewContainerRef.element == null ? null : ctx.config.viewContainerRef.element.nativeElement) !== null && tmp_6_0 !== undefined ? tmp_6_0 : null);
            i0.ɵɵadvance();
            i0.ɵɵproperty("tabIndex", -1);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("polymorpheusOutlet", ctx.content);
          }
        },
        dependencies: [NgDocOverlayPointerComponent, NgDocEventSwitcherDirective, NgDocFocusControlComponent, NgDocFocusCatcherDirective, PolymorpheusModule, i2.PolymorpheusOutletDirective],
        styles: ["[_nghost-%COMP%]{display:block;height:auto;width:100%}.ng-doc-overlay-content[_ngcontent-%COMP%]{width:100%;height:100%;background-color:var(--ng-doc-overlay-background);border:1px solid var(--ng-doc-overlay-border);border-radius:var(--ng-doc-overlay-border-radius);box-shadow:var(--ng-doc-overlay-shadow);overflow:auto}[data-ng-doc-overlay-position=top][_nghost-%COMP%]{transform-origin:bottom}[data-ng-doc-overlay-position=top][data-ng-doc-overlay-with-contact-border=false][_nghost-%COMP%]   .ng-doc-overlay-content[_ngcontent-%COMP%]{border-bottom:none}[data-ng-doc-overlay-position=bottom][_nghost-%COMP%]{transform-origin:top}[data-ng-doc-overlay-position=bottom][data-ng-doc-overlay-with-contact-border=false][_nghost-%COMP%]   .ng-doc-overlay-content[_ngcontent-%COMP%]{border-top:none}[data-ng-doc-overlay-position=left][_nghost-%COMP%]{transform-origin:right}[data-ng-doc-overlay-position=left][data-ng-doc-overlay-with-contact-border=false][_nghost-%COMP%]   .ng-doc-overlay-content[_ngcontent-%COMP%]{border-right:none}[data-ng-doc-overlay-position=right][_nghost-%COMP%]{transform-origin:left}[data-ng-doc-overlay-position=right][data-ng-doc-overlay-with-contact-border=false][_nghost-%COMP%]   .ng-doc-overlay-content[_ngcontent-%COMP%]{border-left:none}"],
        changeDetection: 0
      });
    }
  }
  return NgDocOverlayContainerComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NgDocOverlayContainerComponent };
